// @ts-nocheck
import type { ViewerAPI, Component, Callback } from '@wix/viewer-manager-interface'
import type { BuilderRefSchemas } from '@wix/thunderbolt-becky-root'
import { createReferencelessDeepProxy } from './referencelessDeepProxy'
import { createNoopUpdateStructureOptimizer, createUpdateStructureOptimizer } from './updateStructureOptimizer'
import { isExperimentOpen } from '@wix/thunderbolt-commons'

const apiInvalidatedSym = Symbol('invalidated')

type SiteContext = {
	hasPagesTransition: boolean
}

type PageData = {
	isLightbox: boolean
}

/**
 * this proxy wrapper allow us to replace the viewerAPI instance without refactoring both TB and DM code.
 * This opens up the ability to nullify old viewer state to free up memory before re-creating a fresh instance
 * with the bare essentials for the page
 */
export async function wrapWithProxy({ isResponsive, experiments }: {
	isResponsive: boolean
	experiments: Record<string, any>
}, createViewerAPI: () => Promise<ViewerAPI>) {
	let api: ViewerAPI = await createViewerAPI()
	let siteModelsFetcher, pagesModelsFetcher, prevReplaceViewerCallPageId
	const dynamicRefSchemas: Record<string, BuilderRefSchemas> = {}
	const notifyApplicationRequestFromViewerWorkerCallbacks: Array<Callback> = []
	const appInstanceUpdateCallbacks: Array<Callback> = []

	const previewFrameInitialUrl = location.href
	const siteContext: SiteContext = { hasPagesTransition: false }
	const pagesData: { [pageId: string]: PageData } = {}
	const updateStructureOptimizer = isResponsive && (isExperimentOpen('specs.thunderbolt.optimizeUpdateStructureCalls', experiments) || isExperimentOpen('specs.thunderbolt.optimizeUpdateStructureCalls_vip', experiments)) ? createUpdateStructureOptimizer(api) : createNoopUpdateStructureOptimizer(api)

	if (typeof api.runtime.setGlobalModelsFetcher === 'undefined') {
		// TODO backwards compatability - remove after viewer and DM are both in GA
		return api
	}

	const resetBrowserGlobals = () => {
		// set preview frame's url back to it's initial state so relative navigations will work as expected
		history.replaceState(null, '', previewFrameInitialUrl)
	}

	const reapplyOneTimeActions = () => {
		// Re-register callbacks and configurations that were lost during instance replacement
		// This solves the issue where some functions are being called only once
		api.runtime.setPagesModelsFetcher(pagesModelsFetcher)
		api.runtime.setGlobalModelsFetcher(siteModelsFetcher)
		api.runtime.updateDynamicRefSchemas(dynamicRefSchemas)
		notifyApplicationRequestFromViewerWorkerCallbacks.forEach((cb) => api.platform.registerToNotifyApplicationRequestFromViewerWorker(cb))
		appInstanceUpdateCallbacks.forEach((cb) => api.platform.registerToAppInstanceUpdate(cb))
	}

	const replaceInstance = async () => {
		if (api[apiInvalidatedSym]) {
			console.error(new Error('attempting to invalidate an already invalidated viewerApi'))
		}
		console.log('replaceInstance')
		// since our viewerApi creation looks at the browser globals to populate some of its configs, this must be called before createViewerAPI
		resetBrowserGlobals()
		const newApi = await createViewerAPI()
		api.unmountViewer()
		api[apiInvalidatedSym] = true
		api = null // free memory
		api = newApi
		updateStructureOptimizer.reset(api)
	}

	if (window.debugApi) {
		if (window.debugApi.viewerApiProxy) {
			console.warn('window.getViewerApi called more than once')
		}
		window.debugApi.viewerApiProxy = {
			clearInstance: () => {
				api.unmountViewer()
				api = null
			},
			initInstance: async () => {
				api = await createViewerAPI()
			},
		}
	}

	const proxyAPI = createReferencelessDeepProxy(() => api, {
		data: {
			updateData: (type: any, id: string, item: any, ...rest: Array<any>) => {
				updateStructureOptimizer.onUpdateData(type, id, item)
				if (item?.type === 'PageGroupProperties') {
					siteContext.hasPagesTransition = item.transition && item.transition.toLowerCase() !== 'none'
				}
				if (item?.type === 'Page') {
					const pageData: PageData = {
						isLightbox: item.isPopup,
					}
					pagesData[item.id] ??= pageData
				}
				return api.data.updateData(type, id, item, ...rest)
			},
		},
		structure: {
			updateStructure(id: string, value: Component | undefined) {
				updateStructureOptimizer.updateStructure(id, value)
			},
		},
		runtime: {
			replaceViewer: async (pendingPageId?: string) => {
				if (!pendingPageId) {
					// first navigation or cases where pageId is not provided to setWantedNavInfo
					console.log('viewerApi.replaceViewer skipped because there is no pendingPageId')
					return
				}

				const pageData = pagesData[pendingPageId]
				if (!pageData) {
					console.warn(
						'viewerApi.replaceViewer invoked for page before it\'s page data was set',
						pendingPageId,
					)
				}

				const shouldSkipReplaceInstance = () => {
					if (!pageData) {
						return 'pageData not set'
					}
					if (pageData.isLightbox) {
						return 'page is a lightbox'
					}
					if (siteContext.hasPagesTransition) {
						return 'site has pages transition turn on'
					}
					if (prevReplaceViewerCallPageId === pendingPageId) {
						return `consecutive calls with the same page id (${pendingPageId})`
					}
				}

				const shouldSkipReplaceInstanceReason = shouldSkipReplaceInstance()

				if (!shouldSkipReplaceInstanceReason) {
					try {
						prevReplaceViewerCallPageId = pendingPageId
						await replaceInstance()
						console.log('viewerApi.replaceViewer successful')
					} catch (e) {
						console.error('viewerApi.replaceViewer failed', e)
					}
					reapplyOneTimeActions()

					await siteModelsFetcher()
					await pagesModelsFetcher(['masterPage'], 'DESKTOP').then((fetcher) => fetcher())
				} else {
					console.log(
						`viewerApi.replaceViewer.skipped because ${shouldSkipReplaceInstanceReason} (pageId: ${pendingPageId})`,
					)
				}
			},
			setPagesModelsFetcher: (fetcher) => {
				pagesModelsFetcher = fetcher
				return api.runtime.setPagesModelsFetcher(fetcher)
			},
			setGlobalModelsFetcher: (fetcher) => {
				siteModelsFetcher = fetcher
				return api.runtime.setGlobalModelsFetcher(fetcher)
			},
			updateDynamicRefSchemas: (dynamicRefsByAppDefId: Record<string, BuilderRefSchemas>) => {
				Object.assign(dynamicRefSchemas, dynamicRefsByAppDefId)
				return api.runtime.updateDynamicRefSchemas(dynamicRefsByAppDefId)
			},
		},
		platform: {
			registerToNotifyApplicationRequestFromViewerWorker: (cb: Callback) => {
				notifyApplicationRequestFromViewerWorkerCallbacks.push(cb)
				return api.platform.registerToNotifyApplicationRequestFromViewerWorker(cb)
			},
			registerToAppInstanceUpdate: (cb: Callback) => {
				appInstanceUpdateCallbacks.push(cb)
				return api.platform.registerToAppInstanceUpdate(cb)
			},
		}
	})
	return proxyAPI as any
}
